import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { postJsonAPI } from './utils';

// Live tracking asks a wired device for a fresh fix, over and over, for a
// short window while a recovery worker is watching it. It reuses the saved
// Refresh command, so nothing on the device is reconfigured and nothing needs
// to be restored afterwards: when the window closes, or the page dies, the
// requests simply stop.
export const LIVE_TRACK_DURATION_MS = 5 * 60 * 1000;
const MOVING_INTERVAL_MS = 15 * 1000;
const STOPPED_INTERVAL_MS = 60 * 1000;
const TICK_MS = 5 * 1000;
const MOVING_SPEED_KNOTS = 2;

const isMoving = (position) => position.attributes?.motion === true
  || (position.speed || 0) > MOVING_SPEED_KNOTS;

export default (deviceId, commandId, onError) => {
  const device = useSelector((state) => state.devices.items[deviceId]);
  const position = useSelector((state) => state.session.positions[deviceId]);

  const [endsAt, setEndsAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const lastSentAt = useRef(0);

  const active = endsAt !== null;

  // A different device means a different session. Never carry one across.
  useEffect(() => {
    setEndsAt(null);
    setNow(Date.now());
  }, [deviceId]);

  // The clock only runs during a session. Phone browsers slow timers in the
  // background, which is fine: the end time is absolute, and the visibility
  // listener re-evaluates as soon as the page is back in front.
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const tick = () => setNow(Date.now());
    const interval = setInterval(tick, TICK_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [active]);

  const stop = useCallback(() => setEndsAt(null), []);

  const start = useCallback(() => {
    const current = Date.now();
    lastSentAt.current = 0;
    setEndsAt(current + LIVE_TRACK_DURATION_MS);
    setNow(current);
  }, []);

  // Ask for a fix only when the last one is older than the target interval
  // and nothing has been asked for within that interval. A device already
  // reporting on its own schedule is left alone, and a request that is still
  // being answered is never stacked on.
  useEffect(() => {
    if (!active) {
      return;
    }
    if (now >= endsAt) {
      setEndsAt(null);
      return;
    }
    if (!commandId || !position || device?.status !== 'online') {
      return;
    }
    const interval = isMoving(position) ? MOVING_INTERVAL_MS : STOPPED_INTERVAL_MS;
    const fixAge = now - new Date(position.fixTime).getTime();
    if (fixAge < interval || now - lastSentAt.current < interval) {
      return;
    }
    lastSentAt.current = now;
    postJsonAPI('commands/send', { id: commandId, deviceId }, false, null, (message) => {
      setEndsAt(null);
      if (onError) {
        onError(message);
      }
    });
  }, [active, now, endsAt, commandId, position, device?.status, deviceId]);

  return {
    active,
    remainingMs: active ? Math.max(0, endsAt - now) : 0,
    start,
    stop,
  };
};
